import type { MemberInviteLinkResponse, OrganizerMembersResponse } from '@pulsify/shared'
import { api, unwrap } from '~/shared/lib/rpc'

type MembersTarget = { businessId: string; channelId: string }

const param = ({ businessId, channelId }: MembersTarget) => ({ businessId, channelId })

/** The members of an organizer page and its waiting requests, as only the organizer sees them. */
export const membersApi = {
  async list(target: MembersTarget, q?: string): Promise<OrganizerMembersResponse> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members.$get({
      param: param(target),
      query: q ? { q } : {},
    })
    return unwrap<OrganizerMembersResponse>(res)
  },

  async setEnabled(target: MembersTarget, membersEnabled: boolean): Promise<void> {
    const res = await api.api.businesses[':businessId'].channels[
      ':channelId'
    ].members.settings.$patch({ param: param(target), json: { membersEnabled } })
    await unwrap<null>(res)
  },

  async rotateInvite(target: MembersTarget): Promise<string> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members[
      'invite-link'
    ].$post({ param: param(target) })
    return (await unwrap<MemberInviteLinkResponse>(res)).url
  },

  async approve(target: MembersTarget, memberId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members[
      ':memberId'
    ].approve.$post({ param: { ...param(target), memberId } })
    await unwrap<null>(res)
  },

  async approveAll(target: MembersTarget): Promise<number> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members[
      'approve-all'
    ].$post({ param: param(target) })
    return (await unwrap<{ approved: number }>(res)).approved
  },

  async decline(target: MembersTarget, memberId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members[
      ':memberId'
    ].decline.$post({ param: { ...param(target), memberId } })
    await unwrap<null>(res)
  },

  async remove(target: MembersTarget, memberId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].channels[':channelId'].members[
      ':memberId'
    ].$delete({ param: { ...param(target), memberId } })
    await unwrap<null>(res)
  },
}
